import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, Upload, Check, Ban, X, Hourglass } from 'lucide-react';
import {
  api,
  parseApiError,
  type MatchCardStatus,
  type SubmissionSlotRow,
  type MapVetoState,
} from '../lib/api';
import { cn } from '../lib/cn';
import { clanLabelFrom } from '../lib/displayName';
import { MODE_LABEL } from '../lib/matchCardLabels';
import { submissionWindowOpen } from '../lib/submissionWindow';
import { ImageDropzone } from '../components/ImageDropzone';

// ─────────────────────────────────────────────────────────────────────────────
// PersonalUpload — T3 (personal-upload-squad-select, 2026-07-24).
//
// The mobile-first alternative to piling onto the shared desktop Match Card
// (MatchCardLive.tsx) just to submit a screenshot. A player reaches this from
// their Calendar tile; the page shows ONLY their own slots for this match —
// one row per round, single column, thumb-sized tap targets — and never
// another player's upload box.
//
// Deliberately a THIN wrapper around the same data + endpoints
// MatchCardLive's SlotChip already uses (GET /matchcard/:matchId, then
// filter to slot.playerId === me.discordId; slot upload-url + confirm;
// slot declare-nothing). No new backend surface — this page only changes
// WHERE the same three calls are made from and WHAT is shown.
//
// Submitting here populates the match card ONLY — it does not start OCR.
// OCR stays an admin-triggered bulk batch (see matchCardV2.ts's
// handleSlotConfirm: ocrAutoKicked is permanently false). So the
// post-upload state reads "waiting to be processed", never "processing".
// ─────────────────────────────────────────────────────────────────────────────

const STATUS_LABEL: Partial<Record<MatchCardStatus, string>> = {
  DRAFT: 'Not built yet',
  SCHEDULED: 'Scheduled',
  VETO: 'Veto open',
  LIVE: 'Live — submit now',
  CLOSED: 'Closed — awaiting OCR',
  OCR: 'Processing',
  VERIFIED: 'Verified',
  FORGED: 'Forged',
  CANCELLED: 'Cancelled',
  DISPUTED: 'Disputed',
  FAILED: 'Failed',
  MANUAL: 'Manual scoring',
};

export function PersonalUpload() {
  const { matchId: rawMatchId } = useParams();
  const [qs] = useSearchParams();
  const compId = qs.get('compId') || '';
  const matchId = rawMatchId ? decodeURIComponent(rawMatchId) : '';

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me, retry: false });

  const cardQuery = useQuery({
    queryKey: ['matchcard', matchId, compId],
    queryFn: () => api.matchCardGet(matchId, compId),
    enabled: !!matchId && !!compId,
    refetchInterval: 5000,
  });

  if (!matchId || !compId) {
    return <Shell matchId={matchId} compId={compId}><Empty text="Missing match or competition id." /></Shell>;
  }
  if (cardQuery.isLoading) {
    return <Shell matchId={matchId} compId={compId}><Empty text="Loading your match…" /></Shell>;
  }
  if (cardQuery.isError || !cardQuery.data) {
    return <Shell matchId={matchId} compId={compId}><Empty text="Match not found." kind="error" /></Shell>;
  }
  if (!me?.discordId) {
    return <Shell matchId={matchId} compId={compId}><Empty text="Loading your profile…" /></Shell>;
  }

  const { match, slots } = cardQuery.data;
  const cardStatus: MatchCardStatus = (match.cardStatus ?? 'DRAFT') as MatchCardStatus;
  // Kickoff time, for the pre-kickoff half of the submission window — see
  // RoundRow's isOpenWindow. Undefined on a match with no agreed date yet,
  // which submissionWindowOpen treats as closed (fail-closed by design).
  const scheduledAt = match.scheduledAt as number | undefined;
  // Whether this player could submit RIGHT NOW if they had a slot — the same
  // predicate the server gates on, so the "not open yet" copy below can never
  // claim the window is shut while the backend is accepting uploads.
  const windowOpen = submissionWindowOpen(cardStatus, scheduledAt, Date.now());
  const clan1 = clanLabelFrom(match.clan1DisplayName, match.clan1);
  const clan2 = clanLabelFrom(match.clan2DisplayName, match.clan2);

  // Identity-scoped (T3 requirement 2) — ONLY this player's own slots, never
  // another player's. One slot per game they're fielded for (a squad member
  // gets a slot for EVERY game the series plans, per T1b's roster-init
  // panel), sorted so Game 1 always leads.
  const mySlots = [...(slots ?? [])]
    .filter(s => s.playerId === me.discordId)
    .sort((a, b) => a.gameNum - b.gameNum);

  // T2c — "Subbing in?" candidates for a REGISTERED clan member who has no
  // (or not all) slots of their own for this match. See the mySide comment
  // just below for the side-detection judgment call.
  const myGameNums = new Set(mySlots.map(s => s.gameNum));
  const stripPfx = (s: string | undefined) => (s || '').replace(/^CLAN#/, '');
  const clan1Bare = stripPfx(match.clan1);
  const clan2Bare = stripPfx(match.clan2);
  // `me.clanName` is derived server-side (getRole in portalApi/index.ts) from
  // ONE live CLAN#.../USER# membership row for this discordId — the SAME
  // authority the T2 backend's isRegisteredClanMemberGate/resolveSubSide
  // check against, so when it resolves AND matches one of this match's two
  // clans, the side read here is exactly what the server will independently
  // re-derive (never trusted as a substitute for that server check — just
  // used to pick which rounds to SHOW). A multi-clan member whose primary
  // clanName isn't this match's clan, or an admin/founder with no clan row,
  // can't be cleanly placed on a side from the client alone — for THOSE we
  // degrade to showing open rounds from either side and let the sub-upload
  // endpoint's fail-closed 403 ("Not a registered member of either clan in
  // this match") be the real gate. Either way the actual claim always goes
  // through the server's own membership check; this only shapes the list.
  const myClanBare = stripPfx(me.clanName);
  const mySide: 1 | 2 | null =
    myClanBare && myClanBare === clan1Bare ? 1 :
    myClanBare && myClanBare === clan2Bare ? 2 : null;

  // One row per OPEN (PENDING) game the viewer doesn't already own — a game
  // they already have a slot for (fielded, or a prior sub-claim) is excluded
  // so it never double-shows here AND under "your rounds" above; that's the
  // same (gameNum, ctx.discordId) key handleSubSlotUploadUrl itself checks
  // server-side before allowing a claim. Deduped by gameNum (a side can have
  // several teammates' slots still PENDING for the same game — one row is
  // enough; the server auto-picks which exact slot a claim lands on).
  const seenSubGames = new Set<number>();
  const openSubRounds: SubmissionSlotRow[] = [];
  for (const s of [...(slots ?? [])].sort((a, b) => a.gameNum - b.gameNum)) {
    if (s.status !== 'PENDING') continue;
    if (myGameNums.has(s.gameNum)) continue;
    if (seenSubGames.has(s.gameNum)) continue;
    if (mySide !== null && stripPfx(s.clanId) !== (mySide === 1 ? clan1Bare : clan2Bare)) continue;
    seenSubGames.add(s.gameNum);
    openSubRounds.push(s);
  }

  return (
    <Shell matchId={matchId} compId={compId}>
      <header className="mb-5">
        <h1 className="font-display text-lg font-extrabold uppercase tracking-wide text-white">
          {clan1} <span className="text-iron-500">vs</span> {clan2}
        </h1>
        <p className="mt-1 flex items-center gap-2 font-mono text-xs text-iron-400">
          Your screenshots for this match
          <span className="text-iron-600">·</span>
          <span>{STATUS_LABEL[cardStatus] ?? cardStatus}</span>
        </p>
      </header>

      {mySlots.length === 0 ? (
        <Empty
          text={
            !windowOpen && (cardStatus === 'DRAFT' || cardStatus === 'SCHEDULED' || cardStatus === 'VETO')
              ? "This match hasn't opened for submissions yet — check back an hour before kick-off."
              : openSubRounds.length > 0
                ? 'You have no screenshots of your own to submit for this match yet.'
                : 'You have no screenshots to submit for this match.'
          }
        />
      ) : (
        <ul className="space-y-3">
          {mySlots.map(slot => (
            <RoundRow
              key={slot.sk}
              slot={slot}
              cardStatus={cardStatus}
              scheduledAt={scheduledAt}
              matchId={matchId}
              compId={compId}
              discordId={me.discordId}
              mapVeto={match.mapVeto as MapVetoState | undefined}
            />
          ))}
        </ul>
      )}

      {/* T2c — additive, never shown for a game the viewer already owns a
          slot for (see openSubRounds' construction above). */}
      {openSubRounds.length > 0 && (
        <section className="mt-6">
          <h2 className="font-display text-sm font-bold uppercase tracking-wider text-white">Subbing in?</h2>
          <p className="mt-1 font-mono text-xs text-iron-400">
            Subbed in? Upload your screenshot for the round you played.
          </p>
          <ul className="mt-3 space-y-3">
            {openSubRounds.map(slot => (
              <RoundRow
                key={`sub-${slot.gameNum}`}
                slot={slot}
                variant="sub"
                cardStatus={cardStatus}
                scheduledAt={scheduledAt}
                matchId={matchId}
                compId={compId}
                discordId={me.discordId}
                mapVeto={match.mapVeto as MapVetoState | undefined}
              />
            ))}
          </ul>
        </section>
      )}
    </Shell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function Shell({ children, matchId, compId }: { children: React.ReactNode; matchId: string; compId: string }) {
  return (
    <div className="mx-auto max-w-md px-4 py-6">
      <div className="mb-4 flex items-center justify-between gap-3">
        <Link to="/calendar" className="inline-flex items-center gap-1 text-sm text-iron-400 hover:text-iron-200">
          <ChevronLeft size={16} /> Calendar
        </Link>
      </div>
      {children}
      {/* Escape hatch to the full desktop card — captains/leaders/admins
          still need veto, roster init, comments, forge, etc. This page is
          deliberately a narrow slice of that surface, not a replacement. */}
      {matchId && compId && (
        <div className="mt-8 border-t border-iron-800 pt-4 text-center">
          <Link
            to={`/matchcard/${encodeURIComponent(matchId)}?compId=${encodeURIComponent(compId)}`}
            className="font-mono text-xs text-iron-500 underline hover:text-iron-300"
          >
            View full match card
          </Link>
        </div>
      )}
    </div>
  );
}

function Empty({ text, kind = 'info' }: { text: string; kind?: 'info' | 'error' }) {
  return (
    <div className="rounded-xl border border-iron-700 bg-iron-900 px-6 py-12 text-center">
      <p className={cn('font-mono text-sm', kind === 'error' ? 'text-red-400' : 'text-iron-400')}>{text}</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RoundRow — one game, one row. Three states, never a bare empty button:
//   Upload        — pick a screenshot; sends the moment it's picked
//                    (upload-url -> PUT -> confirm, same as SlotChip).
//   ✓ Submitted   — already uploaded (UPLOADED/OCR_PROCESSING/OCR_DONE/
//                    FLAGGED) — "waiting to be processed", never
//                    "processing": OCR is a separate admin-triggered batch.
//   — Not played  — this player's own declare-nothing (slotDeclareNothing),
//                    greyed, no upload expected, doesn't nag.
//
// T2c — variant="sub" reuses this SAME row for a "Subbing in?" candidate
// (a game the viewer isn't fielded for, but an open PENDING slot exists on
// their side). Differences, all localized to the two spots below:
//   - the open window is LIVE-OR-CLOSED (subClaimGate's own window — a sub
//     claim is meant to land AFTER the game, before the admin runs OCR
//     digestion), not LIVE-only like a fielded player's own upload;
//   - upload targets the sub-upload-url/sub-confirm pair (no playerId in the
//     path — the server resolves ctx.discordId's membership itself) instead
//     of the per-slot route, and never sends a discordId;
//   - no "declare nothing" affordance — that's a fielded player's own-slot
//     action, meaningless for a slot the viewer doesn't own (yet).
// A sub-variant row is only ever handed a PENDING slot (PersonalUpload's
// openSubRounds filters to that) — the isSubmitted/isMissed branches below
// are simply inert for it, exactly like a fresh own-slot row.
// ─────────────────────────────────────────────────────────────────────────────

function RoundRow({ slot, cardStatus, scheduledAt, matchId, compId, discordId, mapVeto, variant = 'own' }: {
  slot: SubmissionSlotRow;
  cardStatus: MatchCardStatus;
  /** Kickoff time — feeds submissionWindowOpen's pre-kickoff branch. Absent
   *  on a match with no agreed date yet, where that predicate fails closed. */
  scheduledAt?: number;
  matchId: string;
  compId: string;
  discordId: string;
  mapVeto?: MapVetoState;
  variant?: 'own' | 'sub';
}) {
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [declaring, setDeclaring] = useState(false);
  const [declareReason, setDeclareReason] = useState('');
  const isSub = variant === 'sub';

  // Same map lookup MatchCardLive's ClanSlotTable header uses — the veto'd
  // pick for THIS game, falling back to the slot's own stamped mapName.
  const map = mapVeto?.picks.find(p => p.gameNum === slot.gameNum)?.mapName ?? slot.mapName;
  const label = `Game ${slot.gameNum} · ${MODE_LABEL(slot.gameMode)}`;

  const isSubmitted = slot.status === 'UPLOADED' || slot.status === 'OCR_PROCESSING'
    || slot.status === 'OCR_DONE' || slot.status === 'FLAGGED';
  const isMissed = slot.status === 'MISSED';
  const isPending = slot.status === 'PENDING';
  // Own upload mirrors SlotChip's slotIsOpen exactly — which, since the P2
  // match-night widening (2026-07-25), means submissionWindowOpen rather
  // than a bare `cardStatus === 'LIVE'`. Keeping the old literal here would
  // have made THIS page the binding constraint: the server's
  // handleSlotUploadUrl/handleSlotConfirm/handleDeclareNothing all accept a
  // submission from T-1h before kickoff while the card is still
  // SCHEDULED/VETO, so a hardcoded LIVE check would show a read-only page to
  // a player the backend was perfectly willing to accept — silently
  // withdrawing the pre-kickoff window on the one surface built for
  // uploading. Same predicate, same arguments, same answer as the server.
  //
  // A sub claim's window is deliberately NOT this predicate (matches
  // subClaimGate in matchCardV2.ts): LIVE OR CLOSED. Wider at the back — a
  // sub claims a round AFTER the game, up to the admin's explicit "run
  // digestion" click — and narrower at the front, since there is nothing to
  // sub INTO before kickoff.
  const isOpenWindow = isSub
    ? (cardStatus === 'LIVE' || cardStatus === 'CLOSED')
    : submissionWindowOpen(cardStatus, scheduledAt, Date.now());

  const declareMutation = useMutation({
    mutationFn: (reason: string) => api.slotDeclareNothing(matchId, slot.gameNum, discordId, compId, reason || undefined),
    onSuccess: () => {
      setDeclaring(false);
      setDeclareReason('');
      void queryClient.invalidateQueries({ queryKey: ['matchcard', matchId, compId] });
    },
    onError: (e: unknown) => setError(parseApiError(e).message),
  });

  // Send-each-as-picked: the same upload-url -> PUT -> confirm sequence
  // SlotChip's handleUpload uses, fired the instant a file is chosen. The
  // sub variant calls the T2 sub-upload-url/sub-confirm pair instead (no
  // discordId in the path — the server resolves it from the JWT), and hands
  // the replacedPlayerId the FIRST call resolved straight to the second, per
  // T2's contract (never re-derived client-side).
  const handleUpload = async (file: File) => {
    setError('');
    setUploading(true);
    try {
      const ct = file.type as 'image/png' | 'image/jpeg' | 'image/webp';
      if (isSub) {
        const { url, s3Key, replacedPlayerId } = await api.subUploadUrl(matchId, slot.gameNum, compId, ct);
        const putRes = await fetch(url, { method: 'PUT', body: file, headers: { 'Content-Type': ct } });
        if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);
        await api.subConfirm(matchId, slot.gameNum, compId, s3Key, replacedPlayerId);
      } else {
        const { url, s3Key } = await api.slotUploadUrl(matchId, slot.gameNum, discordId, compId, ct);
        const putRes = await fetch(url, { method: 'PUT', body: file, headers: { 'Content-Type': ct } });
        if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);
        await api.slotConfirm(matchId, slot.gameNum, discordId, compId, s3Key);
      }
      await queryClient.invalidateQueries({ queryKey: ['matchcard', matchId, compId] });
    } catch (e: unknown) {
      setError(parseApiError(e).message);
    } finally {
      setUploading(false);
    }
  };

  const missedDetail = !isMissed ? null
    : slot.missedVia === 'timeout' ? 'No submission received before close'
    : slot.reason ? `Declared — ${slot.reason}`
    : 'Declared — nothing to submit';

  return (
    <li className="rounded-xl border-2 border-iron-700 bg-iron-900 p-4">
      <div className="mb-3">
        <div className="font-display text-sm font-bold uppercase tracking-wider text-white">{label}</div>
        {map && <div className="mt-0.5 font-mono text-xs text-iron-400">{map}</div>}
      </div>

      {isSubmitted && (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-600/50 bg-emerald-900/30 px-4 py-3">
          <Check size={18} className="shrink-0 text-emerald-300" />
          <span className="font-mono text-sm text-emerald-200">
            Submitted — {slot.status === 'OCR_DONE' ? 'processed' : slot.status === 'OCR_PROCESSING' ? 'processing' : 'waiting to be processed'}
          </span>
        </div>
      )}

      {isMissed && (
        <div className="flex items-center gap-2 rounded-lg border border-iron-700 bg-iron-800/60 px-4 py-3">
          <Ban size={16} className="shrink-0 text-iron-500" />
          <span className="font-mono text-sm text-iron-400">Not played{missedDetail ? ` — ${missedDetail}` : ''}</span>
        </div>
      )}

      {isPending && isOpenWindow && (
        <>
          <ImageDropzone onFile={handleUpload} accept="image/png,image/jpeg,image/webp" disabled={uploading}>
            <label
              className={cn(
                'flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg py-4',
                'font-display text-sm font-bold uppercase tracking-wider text-white',
                'bg-forge-600 active:bg-forge-500',
                uploading && 'pointer-events-none opacity-70',
              )}
            >
              <Upload size={18} />
              {uploading ? 'Uploading…' : 'Upload screenshot'}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                disabled={uploading}
                className="hidden"
                onChange={e => {
                  const f = e.target.files?.[0];
                  if (f) void handleUpload(f);
                  e.target.value = '';
                }}
              />
            </label>
          </ImageDropzone>

          {/* "Not played" declares against the viewer's OWN slot — meaningless
              for a sub candidate who doesn't own this slot (yet), so it never
              renders in that variant. */}
          {!isSub && (!declaring ? (
            <button
              type="button"
              onClick={() => setDeclaring(true)}
              className="mt-2 w-full rounded-lg py-2 font-mono text-xs text-iron-500 hover:text-iron-300"
            >
              Didn't play this one? Mark not played
            </button>
          ) : (
            <div className="mt-2 rounded-lg border border-iron-700 bg-iron-950 p-3">
              <input
                type="text"
                value={declareReason}
                onChange={e => setDeclareReason(e.target.value.slice(0, 200))}
                placeholder="Reason (optional)"
                maxLength={200}
                className="w-full border-none bg-transparent font-mono text-xs text-iron-200 placeholder:text-iron-600 focus:outline-none"
              />
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={() => declareMutation.mutate(declareReason.trim())}
                  disabled={declareMutation.isPending}
                  className="flex-1 rounded-lg bg-iron-700 py-2 font-mono text-xs text-iron-100 hover:bg-iron-600"
                >
                  {declareMutation.isPending ? 'Confirming…' : 'Confirm — nothing to submit'}
                </button>
                <button
                  type="button"
                  onClick={() => { setDeclaring(false); setDeclareReason(''); }}
                  className="rounded-lg px-3 py-2 font-mono text-xs text-iron-500 hover:text-iron-300"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          ))}
        </>
      )}

      {isPending && !isOpenWindow && (
        <div className="flex items-center gap-2 rounded-lg border border-iron-800 bg-iron-950 px-4 py-3">
          <Hourglass size={16} className="shrink-0 text-iron-600" />
          <span className="font-mono text-sm text-iron-500">
            {cardStatus === 'DRAFT' || cardStatus === 'SCHEDULED' || cardStatus === 'VETO'
              ? 'Submissions open an hour before kick-off'
              : isSub
                ? 'Sub window has closed for this match'
                : 'Submission window closed'}
          </span>
        </div>
      )}

      {error && <p className="mt-2 font-mono text-xs text-red-400">{error}</p>}
    </li>
  );
}

export default PersonalUpload;
